/**
 * Public e-signature page — opens from the link the admin sent by email or WhatsApp.
 * URL: /firmar/:token
 *
 * Flow: load request info → client reads the 3 consents → types name (+ optional DNI)
 * → draws signature with finger/mouse on canvas → submits → success screen.
 */
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, Eraser, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONSENT_TEXTS = [
  {
    key: 'accepted_disclaimer',
    title: 'Información sobre la haloterapia',
    body: (
      <>
        <p className="mb-2">
          La haloterapia es una actividad de bienestar complementaria que reproduce el
          microclima de las minas de sal mediante la dispersión de micropartículas en el ambiente.
        </p>
        <p className="mb-2">
          Las sesiones pueden ayudar en situaciones de congestión nasal, alergias,
          sensibilidad respiratoria, asma, bronquitis, estrés o piel sensible.
        </p>
        <p>
          <strong>La haloterapia NO sustituye tratamientos médicos.</strong> Ante cualquier
          duda, sigue las indicaciones de tu médico.
        </p>
      </>
    ),
  },
  {
    key: 'accepted_cancellation_policy',
    title: 'Política de reserva y cancelación',
    body: (
      <>
        <p className="mb-2">
          Reserva formalizada con el <strong>50% de pago anticipado</strong>. Cancelación o
          modificación <strong>sin coste hasta 24h antes</strong>.
        </p>
        <p>
          Con menos de 24h, o no asistencia: se pierde el depósito por el bloqueo de la sala.
          Si el centro no pudiera realizar la sesión, se reagenda o se devuelve el 100%.
        </p>
      </>
    ),
  },
  {
    key: 'accepted_data_consent',
    title: 'Tratamiento de datos (RGPD)',
    body: (
      <>
        <p className="mb-2">
          Acepto que HALO Cuevas de Sal trate mis datos personales para gestionar mi reserva,
          contacto, facturación y obligaciones legales, conforme al RGPD (UE 2016/679).
        </p>
        <p>
          Derechos de acceso, rectificación, supresión, oposición y portabilidad escribiendo a
          info@halocuevas.com.
        </p>
      </>
    ),
  },
];

export const SignaturePage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerDni, setSignerDni] = useState('');
  const [consents, setConsents] = useState({
    accepted_disclaimer: false,
    accepted_cancellation_policy: false,
    accepted_data_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/signature/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.already_signed) setDone(true);
      })
      .catch((err) => {
        if (err.response?.status === 410) setError('expired');
        else if (err.response?.status === 404) setError('notfound');
        else setError('unknown');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const sigRef = useRef(null);

  const allConsents = Object.values(consents).every(Boolean);

  const submit = async () => {
    if (!signerName.trim() || signerName.trim().length < 3) {
      toast.error('Indica tu nombre y apellidos');
      return;
    }
    if (!allConsents) {
      toast.error('Debes aceptar los tres consentimientos');
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Por favor firma en el recuadro');
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = sigRef.current.toDataURL();
      await axios.post(`${API}/signature/${token}/sign`, {
        signer_name: signerName.trim(),
        signer_dni: signerDni.trim() || null,
        signature_image_b64: dataUrl,
        ...consents,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la firma');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-halo-cream">
        <Loader2 className="animate-spin text-halo-brown" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-halo-cream px-4" data-testid="signature-error">
        <div className="bg-white border border-halo-taupe/15 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="font-heading text-2xl text-halo-charcoal mb-2">
            {error === 'expired' ? 'Enlace caducado' : 'Enlace no válido'}
          </h1>
          <p className="text-sm text-halo-text mb-4">
            {error === 'expired'
              ? 'El enlace ha pasado de los 7 días. Pídele a HALO que te envíe uno nuevo.'
              : 'No encontramos esta solicitud. Comprueba el enlace o contacta con nosotros.'}
          </p>
          <a href="tel:678901086">
            <Button className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream">
              Llamar a HALO — 678 901 086
            </Button>
          </a>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-halo-cream px-4" data-testid="signature-done">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-halo-taupe/15 rounded-2xl p-10 max-w-md text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="text-green-600" size={48} />
          </div>
          <h1 className="font-heading text-3xl text-halo-charcoal mb-3">¡Gracias!</h1>
          <p className="text-halo-text mb-5">
            Hemos recibido tu firma. Tu reserva queda confirmada — te esperamos en HALO.
          </p>
          <div className="bg-halo-cream rounded-xl p-4 text-xs text-halo-text">
            Conserva este enlace si necesitas comprobar lo firmado.
          </div>
          <Link to="/" className="block mt-5 text-sm text-halo-brown underline">
            Volver a halocuevas.com
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-halo-cream py-8 px-4" data-testid="signature-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <p className="text-xs tracking-widest text-halo-brown uppercase mb-1">
            HALO Cuevas de Sal
          </p>
          <h1 className="font-heading text-3xl text-halo-charcoal flex items-center justify-center gap-2">
            <ShieldCheck size={26} /> Firma de consentimientos
          </h1>
          <p className="text-sm text-halo-text mt-2">
            Hola {info.customer_name}, antes de tu sesión en HALO necesitamos tu firma.
          </p>
        </div>

        {/* Booking card */}
        <div className="bg-halo-charcoal text-halo-cream rounded-2xl p-5 mb-5">
          <p className="text-halo-cream/60 text-xs tracking-widest mb-1">TU RESERVA</p>
          <p className="text-2xl font-heading mb-1">
            {info.date} · {info.time}
          </p>
          <p className="text-halo-cream/80 text-sm">
            {info.service_name} · {info.room === 'sala_grande' ? 'Sala 2' : 'Sala 1'} ·
            {' '}{info.num_adults} adultos{info.num_children ? `, ${info.num_children} niños` : ''}
          </p>
        </div>

        {/* Consents */}
        <div className="space-y-3 mb-5">
          {CONSENT_TEXTS.map((c) => (
            <ConsentBlock
              key={c.key}
              title={c.title}
              accepted={consents[c.key]}
              onChange={(v) => setConsents((p) => ({ ...p, [c.key]: v }))}
              testid={`sign-consent-${c.key}`}
            >
              {c.body}
            </ConsentBlock>
          ))}
        </div>

        {/* Signer info */}
        <div className="bg-white border border-halo-taupe/15 rounded-2xl p-5 mb-5 space-y-3">
          <h3 className="font-medium text-halo-charcoal">Tus datos para la firma</h3>
          <div>
            <Label>Nombre y apellidos *</Label>
            <Input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Como aparece en tu DNI"
              data-testid="signer-name"
            />
          </div>
          <div>
            <Label>DNI / NIE <span className="text-halo-text font-normal">(opcional)</span></Label>
            <Input
              value={signerDni}
              onChange={(e) => setSignerDni(e.target.value)}
              placeholder="12345678A"
              data-testid="signer-dni"
            />
          </div>
        </div>

        {/* Signature canvas */}
        <div className="bg-white border border-halo-taupe/15 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-halo-charcoal">Tu firma</h3>
            <button
              type="button"
              onClick={() => sigRef.current?.clear()}
              className="text-xs text-halo-brown hover:underline flex items-center gap-1"
              data-testid="clear-signature"
            >
              <Eraser size={12} /> Borrar
            </button>
          </div>
          <SignatureCanvas ref={sigRef} />
          <p className="text-xs text-halo-text mt-2 text-center">
            Firma con el dedo (móvil/tablet) o con el ratón.
          </p>
        </div>

        <Button
          onClick={submit}
          disabled={submitting || !allConsents}
          className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream py-4"
          data-testid="submit-signature"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Enviando firma...
            </span>
          ) : (
            'Confirmar y firmar'
          )}
        </Button>
        <p className="text-xs text-center text-halo-taupe mt-3">
          Tu firma se registra con fecha, hora, IP y dispositivo (firma electrónica simple).
        </p>
      </div>
    </div>
  );
};

const ConsentBlock = ({ title, accepted, onChange, testid, children }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-halo-taupe/15 rounded-xl p-4">
      <h3 className="font-medium text-halo-charcoal mb-2">{title}</h3>
      <div className={`text-sm text-halo-text leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}>
        {children}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="text-xs text-halo-brown hover:underline mt-2 mb-2"
      >
        {expanded ? 'Ocultar' : 'Leer completo'}
      </button>
      <label className="flex items-center gap-2 text-sm cursor-pointer pt-2 border-t border-halo-taupe/10 mt-1">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid}
        />
        <span className="text-halo-charcoal">He leído y acepto.</span>
      </label>
    </div>
  );
};

// Tiny self-contained signature canvas (no external lib).
const SignatureCanvas = forwardRef(({ height = 200 }, ref) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const emptyRef = useRef(true);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const parent = c.parentElement;
    const cssWidth = parent ? parent.clientWidth : 600;
    c.width = cssWidth * ratio;
    c.height = height * ratio;
    c.style.width = `${cssWidth}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, height);
  }, [height]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    emptyRef.current = false;
  };
  const end = () => { drawingRef.current = false; };

  useImperativeHandle(ref, () => ({
    clear: () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
      emptyRef.current = true;
    },
    isEmpty: () => emptyRef.current,
    toDataURL: () => canvasRef.current?.toDataURL('image/png'),
  }));

  return (
    <canvas
      ref={canvasRef}
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
      className="w-full border-2 border-dashed border-halo-taupe/30 rounded-lg touch-none"
      style={{ height: `${height}px`, background: '#fff' }}
      data-testid="signature-canvas"
    />
  );
});

export default SignaturePage;
